import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { theme } from "./theme";
import {
  AtmosphericParticles,
  DynamicSubtitle,
  FadeUp,
  SpecularShine,
} from "./components";

export const KyuStoreExplainerPro: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  // Helper spring shortcut
  const sp = (delay: number, damping = 200) =>
    spring({
      frame,
      fps,
      delay,
      config: { damping },
    });

  // Dynamic Audio Ducking (Remotion Pro pattern)
  // Ducks music during active voiceover (frame 0 to 675)
  const bgmVolume = (f: number) => {
    const fadeIn = interpolate(f, [0, 25], [0, 1], { extrapolateRight: "clamp" });
    const fadeOut = interpolate(f, [durationInFrames - 30, durationInFrames], [1, 0], {
      extrapolateLeft: "clamp",
    });
    const duck = f < 670 ? 0.12 : 0.45;
    return duck * Math.min(fadeIn, fadeOut);
  };

  // Subtitles (Raised to optimal safe band: bottom: 400px)
  const SUBTITLES = [
    { from: 0, to: 52, text: "Apa itu Kyu Store?", highlight: "Apa itu Kyu Store?" },
    { from: 52, to: 215, text: "Kyu Store adalah platform top up game dan voucher resmi seratus persen legal di Indonesia.", highlight: "resmi seratus persen legal" },
    { from: 215, to: 350, text: "Gak perlu login password, cukup masukkan User ID dan Zone ID...", highlight: "Gak perlu login password" },
    { from: 350, to: 535, text: "...bayar instan pakai QRIS, dan diamond langsung masuk ke akunmu dalam nol koma delapan detik!", highlight: "dalam nol koma delapan detik!" },
    { from: 535, to: 720, text: "Mau diamond aman dan promo anti-boncos? Langsung ke kyustore.com!", highlight: "Langsung ke kyustore.com!" },
  ];

  // Camera 3D Parallax Drift across Scenes
  const camRotY = interpolate(frame, [0, 220, 535, 720], [-6, 5, -4, 0], {
    easing: theme.ease,
  });
  const camRotX = interpolate(frame, [0, 220, 535, 720], [6, 2, 4, 0], {
    easing: theme.ease,
  });

  // Specular shine sweeps
  const shineProgress = interpolate(frame % 200, [0, 60], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: theme.ease,
  });

  // Scene 1 Springs (Frames 0 - 220)
  const s1LogoSpring = sp(6, 200);
  const s1BadgeSpring = sp(20, 200);
  const s1DioramaSpring = sp(35, 200);

  // Scene 2 Springs: 3 Core Pillars (Frames 220 - 535)
  const s2Pillar1 = sp(225, 200);
  const s2Pillar2 = sp(260, 200);
  const s2Pillar3 = sp(295, 200);

  // Scene 3 Springs: CTA & Value Offer (Frames 535 - 720)
  const s3Intro = sp(540, 200);
  const ctaPulse = Math.sin((frame - 580) * 0.16) * 0.03 + 1.0;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: theme.colors.bg,
        fontFamily: theme.fonts.headline,
        color: theme.colors.text,
        overflow: "hidden",
      }}
    >
      {/* Audio Layer with Remotion Pro Dynamic Ducking */}
      <Audio src={staticFile("kyustore_explainer_mastered.mp3")} volume={1.0} />
      <Audio src={staticFile("bgm-clean.mp3")} volume={bgmVolume} />

      {/* Synchronized SFX */}
      {frame === 4 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.65} />}
      {frame === 24 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}
      {frame === 225 && <Audio src={staticFile("pop.mp3")} volume={0.7} />}
      {frame === 260 && <Audio src={staticFile("pop.mp3")} volume={0.7} />}
      {frame === 295 && <Audio src={staticFile("ui-success.mp3")} volume={0.8} />}
      {frame === 545 && <Audio src={staticFile("whoosh.mp3")} volume={0.7} />}
      {frame === 600 && <Audio src={staticFile("click.mp3")} volume={0.75} />}

      {/* Background Subtle Tech Grid */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(rgba(93, 63, 211, 0.08) 1.5px, transparent 1.5px),
            linear-gradient(to right, rgba(93, 63, 211, 0.035) 1px, transparent 1px)
          `,
          backgroundSize: "32px 32px, 64px 64px",
          pointerEvents: "none",
        }}
      />

      {/* Ambient Radial Spotlight */}
      <div
        style={{
          position: "absolute",
          top: "45%",
          left: "50%",
          width: 900,
          height: 900,
          transform: "translate(-50%, -50%)",
          background: "radial-gradient(circle, rgba(93, 63, 211, 0.14) 0%, rgba(248, 246, 254, 0) 70%)",
          filter: "blur(90px)",
          pointerEvents: "none",
        }}
      />

      {/* Top Header HUD Bar */}
      <div
        style={{
          position: "absolute",
          top: 80,
          left: theme.spacing.page,
          right: theme.spacing.page,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 50,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Img src={staticFile("kyu_logo.png")} style={{ height: 48, objectFit: "contain" }} />
          <div style={{ width: 2, height: 26, backgroundColor: "#CBD5E1" }} />
          <span
            style={{
              fontFamily: theme.fonts.technical,
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: "0.12em",
              color: theme.colors.muted,
            }}
          >
            [ OFFICIAL EXPLAINER ]
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            backgroundColor: theme.colors.surface,
            border: `2px solid ${theme.colors.success}`,
            padding: "8px 22px",
            borderRadius: 24,
            boxShadow: "0 4px 15px rgba(16, 185, 129, 0.15)",
          }}
        >
          <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: theme.colors.success, boxShadow: "0 0 10px #10B981" }} />
          <span
            style={{
              fontFamily: theme.fonts.technical,
              fontSize: 18,
              fontWeight: 700,
              color: theme.colors.success,
              letterSpacing: "0.08em",
            }}
          >
            MOONTON DIRECT PARTNER
          </span>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 3D PERSPECTIVE VIEWPORT (1400px)                                          */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          perspective: 1400,
          perspectiveOrigin: "50% 48%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "relative",
            width: 900,
            height: 1100,
            transformStyle: "preserve-3d",
            transform: `rotateY(${camRotY}deg) rotateX(${camRotX}deg)`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >

          {/* ===================================================================== */}
          {/* SCENE 1: HOOK & OFFICIAL IDENTITY (Frames 0 - 220)                    */}
          {/* ===================================================================== */}
          {frame < 225 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(frame, [205, 222], [1, 0], { extrapolateLeft: "clamp" }),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              {/* Question Hook Badge */}
              <FadeUp delay={4}>
                <div
                  style={{
                    backgroundColor: theme.colors.accentLight,
                    border: `2px solid ${theme.colors.accent}`,
                    borderRadius: 24,
                    padding: "8px 28px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 18,
                    boxShadow: "0 6px 20px rgba(93, 63, 211, 0.18)",
                  }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.accent} strokeWidth="3">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 800, color: theme.colors.accent, letterSpacing: "0.08em" }}>
                    PROFIL RESMI PLATFORM
                  </span>
                </div>
              </FadeUp>

              {/* Display Headline */}
              <FadeUp delay={10}>
                <h1
                  style={{
                    fontFamily: theme.fonts.display,
                    fontSize: 78,
                    textAlign: "center",
                    lineHeight: 0.98,
                    marginBottom: 24,
                    textShadow: "0 10px 30px rgba(0,0,0,0.06)",
                  }}
                >
                  APA ITU <span style={{ color: theme.colors.accent }}>KYU STORE?</span>
                </h1>
              </FadeUp>

              {/* Master 3D Identity Stage */}
              <div
                style={{
                  position: "relative",
                  width: 840,
                  height: 520,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "40px",
                  transformStyle: "preserve-3d",
                  transform: `scale(${interpolate(s1DioramaSpring, [0, 1], [0.85, 1])})`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Big Kyu Logo Unit */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 22,
                    marginBottom: 26,
                    transform: `translateZ(40px) scale(${interpolate(s1LogoSpring, [0, 1], [0.8, 1])})`,
                  }}
                >
                  <Img
                    src={staticFile("kyu_logo.png")}
                    style={{
                      height: 110,
                      objectFit: "contain",
                      filter: "drop-shadow(0 14px 28px rgba(93, 63, 211, 0.3))",
                    }}
                  />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontFamily: theme.fonts.display, fontSize: 58, color: theme.colors.text, lineHeight: 0.95 }}>
                      KYU STORE INDONESIA
                    </span>
                    <span style={{ fontFamily: theme.fonts.technical, fontSize: 20, fontWeight: 700, color: theme.colors.success, letterSpacing: "0.08em" }}>
                      PLATFORM TOP UP GAME 100% RESMI & LEGAL
                    </span>
                  </div>
                </div>

                {/* Badges Bar (Mythical Glory Rank & 3D Diamond) */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 20,
                    transform: `translateZ(55px) scale(${interpolate(s1BadgeSpring, [0, 1], [0.85, 1])})`,
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      backgroundColor: theme.colors.surface,
                      border: "2px solid #E2E8F0",
                      borderRadius: 22,
                      padding: "16px 22px",
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                    }}
                  >
                    <Img src={staticFile("official_rank_mythical_glory.png")} style={{ width: 62, height: 62, objectFit: "contain" }} />
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 16, fontWeight: 700, color: theme.colors.muted }}>
                        GARANSI AKUN
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 28, color: theme.colors.text, letterSpacing: "0.02em" }}>
                        ANTI-MINUS MOONTON
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      flex: 1,
                      backgroundColor: theme.colors.darkBadge,
                      color: "#FFFFFF",
                      borderRadius: 22,
                      padding: "16px 22px",
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      boxShadow: "0 14px 32px rgba(30, 18, 74, 0.35)",
                    }}
                  >
                    <Img src={staticFile("diamond_3d_crystal.png")} style={{ width: 54, height: 54, objectFit: "contain" }} />
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 16, fontWeight: 700, color: "#00F0FF" }}>
                        PROSES TRANSAKSI
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 28, color: "#FFFFFF", letterSpacing: "0.02em" }}>
                        OTOMATIS 0.8 DETIK
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom Catalog Bar inside Card */}
                <div
                  style={{
                    width: "100%",
                    backgroundColor: "rgba(248, 246, 254, 0.92)",
                    border: "1.5px solid #E2E8F0",
                    borderRadius: 20,
                    padding: "14px 28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: 22,
                    transform: "translateZ(35px)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontFamily: theme.fonts.technical, fontSize: 16, fontWeight: 700, color: theme.colors.accent }}>
                      KATALOG GAME RESMI:
                    </span>
                    <span style={{ fontFamily: theme.fonts.headline, fontSize: 19, fontWeight: 800, color: theme.colors.text }}>
                      MLBB • FREE FIRE • VALORANT • GENSHIN • HOK
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: theme.fonts.technical, fontSize: 15, fontWeight: 700, color: theme.colors.success }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>24 JAM INSTAN</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* SCENE 2: THE 3 CORE PILLARS (Frames 220 - 535)                         */}
          {/* ===================================================================== */}
          {frame >= 215 && frame < 540 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(
                  frame,
                  [215, 230, 520, 538],
                  [0, 1, 1, 0],
                  { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
                ),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  backgroundColor: theme.colors.successLight,
                  border: `2px solid ${theme.colors.success}`,
                  padding: "6px 24px",
                  borderRadius: 22,
                  marginBottom: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 15px rgba(16, 185, 129, 0.15)",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                </svg>
                <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.success, letterSpacing: "0.08em" }}>
                  3 ALASAN GAMER PILIH KYU STORE
                </span>
              </div>

              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 72,
                  textAlign: "center",
                  lineHeight: 1.0,
                  marginBottom: 20,
                }}
              >
                KENAPA TOP UP DI <span style={{ color: theme.colors.accent }}>KYU STORE LEBIH UNGGUL?</span>
              </h1>

              {/* The 3 Stacked Pillar Cards */}
              <div
                style={{
                  position: "relative",
                  width: 840,
                  height: 520,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "28px 36px",
                  transformStyle: "preserve-3d",
                  display: "flex",
                  flexDirection: "column",
                  gap: 18,
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Pillar 1: No Password */}
                <div
                  style={{
                    backgroundColor: theme.colors.surface,
                    borderRadius: 24,
                    border: "2px solid #E2E8F0",
                    padding: "18px 28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transform: `translateZ(30px) scale(${interpolate(s2Pillar1, [0, 1], [0.88, 1])})`,
                    boxShadow: "0 10px 25px rgba(0,0,0,0.04)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <div style={{ width: 52, height: 52, borderRadius: 16, backgroundColor: theme.colors.successLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 16, fontWeight: 700, color: theme.colors.muted }}>
                        1. AMAN MAKSIMAL
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 34, color: theme.colors.text }}>
                        CUKUP USER ID & ZONE ID (TANPA PASSWORD)
                      </div>
                    </div>
                  </div>
                  <div style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 800, color: theme.colors.success }}>
                    ANTI-HACK
                  </div>
                </div>

                {/* Pillar 2: QRIS Instant Pay */}
                <div
                  style={{
                    backgroundColor: theme.colors.surface,
                    borderRadius: 24,
                    border: "2px solid #E2E8F0",
                    padding: "18px 28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transform: `translateZ(45px) scale(${interpolate(s2Pillar2, [0, 1], [0.88, 1])})`,
                    boxShadow: "0 10px 25px rgba(0,0,0,0.04)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <div style={{ width: 52, height: 52, borderRadius: 16, backgroundColor: theme.colors.accentLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={theme.colors.accent} strokeWidth="3">
                        <rect x="3" y="3" width="7" height="7"></rect>
                        <rect x="14" y="3" width="7" height="7"></rect>
                        <rect x="14" y="14" width="7" height="7"></rect>
                        <rect x="3" y="14" width="7" height="7"></rect>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 16, fontWeight: 700, color: theme.colors.accent }}>
                        2. PEMBAYARAN OTOMATIS
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 34, color: theme.colors.text }}>
                        SCAN QRIS & SEMUA E-WALLET TERVERIFIKASI
                      </div>
                    </div>
                  </div>
                  <div style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 800, color: theme.colors.accent }}>
                    INSTAN
                  </div>
                </div>

                {/* Pillar 3: 0.82s Inject with Tabular Numbers */}
                <div
                  style={{
                    backgroundColor: theme.colors.darkBadge,
                    color: "#FFFFFF",
                    borderRadius: 26,
                    padding: "20px 28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transform: `translateZ(65px) scale(${interpolate(s2Pillar3, [0, 1], [0.88, 1])})`,
                    boxShadow: "0 22px 50px rgba(30, 18, 74, 0.4)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <div style={{ width: 52, height: 52, borderRadius: 16, backgroundColor: "rgba(16, 185, 129, 0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3.5">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 16, fontWeight: 700, color: "#00F0FF" }}>
                        3. TEKNOLOGI MOONTON CLOUD API
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 36, letterSpacing: "0.02em" }}>
                        DIAMOND MASUK DALAM{" "}
                        <span style={{ fontVariantNumeric: "tabular-nums", color: theme.colors.success }}>
                          0.82 DETIK
                        </span>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      backgroundColor: theme.colors.success,
                      color: "#FFFFFF",
                      padding: "8px 18px",
                      borderRadius: 14,
                      fontFamily: theme.fonts.technical,
                      fontSize: 16,
                      fontWeight: 800,
                    }}
                  >
                    REALTIME
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* SCENE 3: CTA & VALUE PAYOFF (Frames 535 - 720)                        */}
          {/* ===================================================================== */}
          {frame >= 530 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(frame, [530, 545], [0, 1], { extrapolateLeft: "clamp" }),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  backgroundColor: theme.colors.goldLight,
                  border: `2px solid ${theme.colors.gold}`,
                  padding: "6px 24px",
                  borderRadius: 22,
                  marginBottom: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 15px rgba(255, 176, 32, 0.2)",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2.5">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                </svg>
                <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: "#92400E", letterSpacing: "0.08em" }}>
                  HARGA RESMI PROMO FLASH SALE
                </span>
              </div>

              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 74,
                  textAlign: "center",
                  lineHeight: 1.0,
                  marginBottom: 24,
                }}
              >
                TOP UP SEKARANG <span style={{ color: theme.colors.accent }}>DI KYUSTORE.COM</span>
              </h1>

              {/* Master CTA Card */}
              <div
                style={{
                  position: "relative",
                  width: 840,
                  height: 520,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "36px 40px",
                  transformStyle: "preserve-3d",
                  transform: `scale(${interpolate(s3Intro, [0, 1], [0.85, 1])})`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Big Kyu Store Brand Unit */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 20,
                    marginBottom: 26,
                    transform: "translateZ(35px)",
                  }}
                >
                  <Img
                    src={staticFile("kyu_logo.png")}
                    style={{
                      height: 110,
                      objectFit: "contain",
                      filter: "drop-shadow(0 12px 28px rgba(93, 63, 211, 0.28))",
                    }}
                  />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontFamily: theme.fonts.display, fontSize: 54, color: theme.colors.text, lineHeight: 0.95 }}>
                      KYU STORE
                    </span>
                    <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.success, letterSpacing: "0.1em" }}>
                      DISTRIBUTOR RESMI MOONTON INDONESIA
                    </span>
                  </div>
                </div>

                {/* Big Glowing CTA Button */}
                <div
                  style={{
                    backgroundColor: theme.colors.success,
                    color: "#FFFFFF",
                    padding: "22px 54px",
                    borderRadius: 26,
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    boxShadow: "0 18px 45px rgba(16, 185, 129, 0.38)",
                    transform: `translateZ(50px) scale(${ctaPulse})`,
                    marginBottom: 26,
                  }}
                >
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3.2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span style={{ fontFamily: theme.fonts.display, fontSize: 42, letterSpacing: "0.04em" }}>
                    KUNJUNGI WEBSITE: KYUSTORE.COM
                  </span>
                </div>

                {/* Trust Points */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 32,
                    fontFamily: theme.fonts.headline,
                    fontSize: 20,
                    fontWeight: 800,
                    color: theme.colors.muted,
                    transform: "translateZ(30px)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>100% Legal & Aman</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>0.82 Detik Satset</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Cukup User ID & Zone</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 18 Atmospheric Floating Light Motes */}
          <AtmosphericParticles frame={frame} count={18} />
        </div>
      </div>

      {/* ========================================================================= */}
      {/* FOOTER TICKER (Safe Zone Bottom 80px)                                     */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          bottom: 80,
          left: theme.spacing.page,
          right: theme.spacing.page,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: theme.colors.surface,
          border: `2px solid ${theme.colors.border}`,
          borderRadius: 22,
          padding: "14px 30px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
          zIndex: 50,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, fontFamily: theme.fonts.headline, fontSize: 18, fontWeight: 800, color: theme.colors.muted }}>
          <span style={{ color: theme.colors.accent }}>METODE RESMI:</span>
          <span>QRIS • BCA • MANDIRI • BRI • DANA • GOPAY • SHOPEEPAY</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: theme.fonts.technical, fontSize: 16, fontWeight: 700, color: theme.colors.success }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>AUTO INJECT ACTIVE 24/7</span>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* DYNAMIC SUBTITLES: PINNED AT OPTIMAL SAFE BAND (bottom: 400px)            */}
      {/* ========================================================================= */}
      <DynamicSubtitle frame={frame} subtitles={SUBTITLES} bottom={400} />
    </AbsoluteFill>
  );
};
